import React, { FC, useMemo } from "react";
import { View, Icon, COLOR } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Button, Text } from "@lookiero/sty-psp-ui";
import { useFormatCurrency } from "../../../../hooks/useFormatCurrency";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { financial } from "../../../../shared/util/financial/financial";
import { style as resumeStyle } from "./Resume.styles";

interface ResumeProps {
  readonly totalPriceAmount: number;
  readonly hideCta?: boolean;
  readonly dark?: boolean;
  readonly hideFreeShipping?: boolean;
  readonly goToCheckout: () => void;
}

const Resume: FC<ResumeProps> = ({
  totalPriceAmount,
  hideCta = false,
  dark = false,
  hideFreeShipping = false,
  goToCheckout,
}) => {
  const basketTotalPriceText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BASKET_TOTAL_PRICE,
  });
  const basketCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BASKET_CTA,
  });
  const basketInfoFreeShippingText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BASKET_INFO_FREE_SHIPPING_AND_RETURN,
  });
  const basketInfoFastDeliveryText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BASKET_INFO_FAST_DELIVERY,
  });
  const resumeReturnTimeText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.RESUME_RETURN_TIME,
  });

  const formatCurrency = useFormatCurrency();

  const style = useMemo(() => resumeStyle(), []);

  return (
    <View style={[style.resume, dark ? style.darkBackground : style.lightBackground]}>
      <View style={style.totalAmountContent}>
        <Text level={2} variant="action">
          {basketTotalPriceText}
        </Text>
        <Text level={2} variant="action">
          {formatCurrency({ amount: financial(totalPriceAmount) })}
        </Text>
      </View>
      {hideCta === false ? (
        <Button
          style={{ button: [style.basketButton, dark ? undefined : { width: "100%" }] }}
          testID="start-checkout-button"
          onPress={goToCheckout}
        >
          {basketCtaText}
        </Button>
      ) : null}
      {hideFreeShipping === false ? (
        <View style={style.infoRow}>
          <Icon color={COLOR.ICON_LIGHT} name="truck" style={style.infoIcon} />
          <Text level={2} style={style.infoText} variant="detail">
            {basketInfoFreeShippingText}
          </Text>
        </View>
      ) : null}
      <View style={style.infoRow}>
        <Icon color={COLOR.ICON_LIGHT} name="zap" style={style.infoIcon} />
        <Text level={2} style={style.infoText} variant="detail">
          {basketInfoFastDeliveryText}
        </Text>
      </View>
      <View style={style.infoRow}>
        <Icon color={COLOR.ICON_LIGHT} name="returns" style={style.infoIcon} />
        <Text level={2} style={style.infoText} variant="detail">
          {resumeReturnTimeText}
        </Text>
      </View>
    </View>
  );
};

export { Resume };
